import { useEffect, useState } from 'react';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { ConnectMetaButton } from './ConnectMetaButton';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Scopes the SlightorCRM backend needs Meta to grant for the various features
 * to function. Mirrors backend's `META_OAUTH_SCOPES` list — keep in sync.
 *
 * If the user's connected ad account is missing any of these, we surface a
 * one-line banner asking them to reconnect (which re-prompts for the new
 * scope list via `auth_type=rerequest`).
 */
const REQUIRED_SCOPES: Array<{ scope: string; feature: string }> = [
  { scope: 'ads_management', feature: 'Manage campaigns + ad sets + ads' },
  { scope: 'ads_read', feature: 'Read campaign performance' },
  { scope: 'pages_show_list', feature: 'Pick a Facebook Page' },
  { scope: 'pages_read_engagement', feature: 'Read Page posts' },
  { scope: 'pages_manage_ads', feature: 'Create + read Lead Forms' },
  { scope: 'pages_manage_metadata', feature: 'Receive Page lead submissions' },
  { scope: 'pages_manage_posts', feature: 'Publish Facebook Page posts' },
  { scope: 'instagram_content_publish', feature: 'Publish Instagram posts' },
  { scope: 'business_management', feature: 'Discover WhatsApp Business Accounts' },
  { scope: 'leads_retrieval', feature: 'Download submitted lead-form data' },
  { scope: 'instagram_basic', feature: 'Use Instagram in ads' },
  { scope: 'instagram_manage_insights', feature: 'Instagram ad performance' },
  { scope: 'whatsapp_business_management', feature: 'Click-to-WhatsApp ad routing' },
  { scope: 'whatsapp_business_messaging', feature: 'Send WhatsApp messages via Cloud API' },
];

const DISMISS_KEY = 'meta_scope_banner_dismissed_at';
/** Re-show the banner after 7 days even if the user dismissed it. */
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Shows a single-line banner above Facebook pages when the connected Meta
 * account is missing scopes the UI's features depend on. Dismissible per-user;
 * reappears after 7 days. Renders nothing when scopes are complete.
 */
export function MetaScopeBanner() {
  const [account, setAccount] = useState<MetaAdAccount | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    facebookAdAccountService.get().then(value => {
      setAccount(value);
      const ts = localStorage.getItem(`${DISMISS_KEY}:${value.companyUid}:${value.accountUid}`);
      setDismissed(!!ts && Date.now() - Number(ts) < DISMISS_TTL_MS);
    }).catch(() => undefined);
  }, []);

  if (!account || dismissed) return null;
  // OAuth-sourced accounts only — manually-pasted tokens skip the check
  // since we can't tell what scopes a hand-pasted token has.
  if (!account.oauthGrantedAt) return null;
  // Until backend records granted scopes (which it now does after the
  // meta-oauth audit fix), older rows have undefined `oauthScopes`. Skip
  // banner for those — they'll get fresh data on next refresh.
  const granted = new Set(account.oauthScopes ?? []);


  const missing = REQUIRED_SCOPES.filter(s => !granted.has(s.scope));
  if (missing.length === 0) return null;

  const dismiss = () => {
    localStorage.setItem(`${DISMISS_KEY}:${account.companyUid}:${account.accountUid}`, String(Date.now()));
    setDismissed(true);
  };


  return (
    <div className="rounded-xl border border-warn/30 bg-warn-soft px-4 py-3 mb-4">
      <div className="flex items-start gap-3">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warn" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-warn">
            Meta account is missing {missing.length} permission{missing.length === 1 ? '' : 's'}
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">
            Reconnect to grant access to: <strong className="text-text-primary">{missing.slice(0, 3).map(m => m.feature).join(', ')}</strong>
            {missing.length > 3 && ` + ${missing.length - 3} more`}
          </p>
          <details className="mt-1.5">
            <summary className="cursor-pointer text-2xs text-text-muted hover:text-text-secondary">Show full list</summary>
            <ul className="mt-1.5 ml-3 space-y-0.5 text-2xs text-text-muted">
              {missing.map(m => (
                <li key={m.scope}>
                  <span className="font-mono">{m.scope}</span> — {m.feature}
                </li>
              ))}
            </ul>
          </details>
        </div>
        <div className="flex shrink-0 gap-1">
          <ConnectMetaButton label="Reconnect" compact onConnected={() => {
            facebookAdAccountService.get().then(setAccount).catch(() => undefined);
          }} />
          <button
            onClick={dismiss}
            className="rounded-md p-1.5 text-text-muted hover:bg-warn/10 hover:text-warn transition-colors"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
